import { Controller } from "@hotwired/stimulus"
import FieldHandler from "../src/field_handler"

export default class extends Controller {
  static targets = ["dropdown", "input", "bubblesContainer", "bubbleTemplate", "component"]
  static values = {
    name: String,
    searchUrl: String,
    frameId: String,
    fieldName: String,
    form: Boolean,
    overlay: Boolean, // true = dropdown w top-layer (natywny popover), nie przycinany przez overflow przodka
    position: String, // left/force_left/right/force_right - kierunek rozwijania (to samo co w trybie non-overlay)
    old: { // wsparcie dla single_select (value), bulk_action (name_id)
      type: Object,
      default: { send: false, value: "", name_id: "" },
    },
    // tryb statyczny: ile pozycji pokazac przy pustym query (0 = wszystkie).
    // Pelna lista i tak jest w DOM do wyszukiwania - limit tylko zaweza widok na start.
    staticLimit: { type: Number, default: 0 },
  }

  searchTimeout = null
  count = 0
  connect() {
    console.log("intum select connect")
    this.frame = document.getElementById(this.frameIdValue)
    this.multiple = this.element.dataset.multiple === "true"

    this.fieldHandler = new FieldHandler(this.element, this.fieldNameValue)

    this.open = false
    this.activeIndex = null
    // otwieranie dropdownu po kliknieciu w input
    this.inputTarget.addEventListener("click", this.showDropdown.bind(this))
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    // zamykanie dropdownu po kliknieciu poza nim. Handler trzymamy w polu, bo .bind() daje
    // za każdym razem NOWĄ funkcję - bez tego removeEventListener w disconnect nic nie zdejmuje
    // i każda podmiana ramki (change_bulk_option po zmianie statusu) zostawia martwy nasłuch.
    this.hideDropdownHandler = this.hideDropdown.bind(this)
    document.addEventListener("click", this.hideDropdownHandler)

    this.frameLoaded = false
    // akcje po zaladowaniu frame'a
    this.frame.addEventListener("turbo:frame-load", () => {
      this.frameLoaded = true
      this.toggleSelection()
      this.hideSpinner()
      // reset highlightu po podmianie wynikow
      this.activeIndex = null
      // panel dostal wlasnie liste (byl waski: input + spinner) - trzeba przeliczyc pozycje overlaya,
      // inaczej rosnie w prawo od left policzonego dla waskiego panelu
      if (this.open && this.useOverlay) { this.positionOverlay() }
    })

    // tryb statyczny (brak search_url) - wyniki sa juz w DOM, nie ma fetchu ani turbo:frame-load
    if (!this.searchUrlValue) {
      this.frameLoaded = true
      this.toggleSelection()
      this.hideSpinner()
      this.applyStaticLimit()
    }
  }

  // tryb statyczny: przy pustym query pokazuje tylko pierwszych staticLimit pozycji (0 = wszystkie).
  // Pelna lista pozostaje w DOM - wpisanie frazy przeszukuje wszystkie pozycje.
  applyStaticLimit() {
    const limit = this.staticLimitValue
    this.resultItems().forEach((item, i) => {
      item.classList.toggle("hidden", limit > 0 && i >= limit)
    })
  }

  // nawigacja klawiatura po wynikach wyszukiwania (analogicznie do autocomplete)
  // ArrowDown/ArrowUp - poruszanie sie, Enter/Tab - wybor, Escape - zamkniecie
  handleKeydown(event) {
    if (event.key === "Enter") { event.preventDefault() }

    // ArrowDown otwiera zamkniety dropdown
    if (event.key === "ArrowDown" && !this.open) { this.showDropdown() }

    if (!this.open) { return }

    const items = this.resultItems()
    if (items.length === 0) { return }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.min(this.activeIndex + 1, items.length - 1)
      this.highlightActive(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.max(this.activeIndex - 1, 0)
      this.highlightActive(items)
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.activeIndex != null && items[this.activeIndex]) {
        event.preventDefault()
        // klik wywoluje data-action: toggleResult + toggleBubble (pelna scieżka wyboru)
        items[this.activeIndex].click()
      }
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.closeDropdown()
      this.activeIndex = null
    }
  }

  // wszystkie wybieralne wyniki w turbo frame
  resultItems() {
    return Array.from(this.frame.querySelectorAll("div[data-value]"))
  }

  // podswietla aktywny element (mocniej niz hover) i scrolluje do widoku
  highlightActive(items) {
    items.forEach((el, i) => el.classList.toggle("bg-gray-200", i === this.activeIndex))
    if (items[this.activeIndex]) { items[this.activeIndex].scrollIntoView({ block: "nearest" }) }
  }

  search(event) {
    // tryb statyczny (brak search_url) - filtrujemy juz wyrenderowane pozycje po stronie klienta.
    // Dopasowanie po widocznej nazwie ORAZ po wartosci (data-value), zeby "pl" lapalo kod PL
    // tak jak "pol" lapie nazwe "polska". Ranking (konwencja jak select2/choices.js): najpierw
    // dopasowania prefiksowe, potem "contains" (substring) - te drugie nizej. Pusty query wraca
    // do limitu startowego.
    if (!this.searchUrlValue) {
      this.showDropdown()
      const q = this.inputTarget.value.trim().toLowerCase()
      if (q === "") { this.applyStaticLimit(); return }

      const items = this.resultItems()
      const prefixMatches = []
      const substringMatches = []
      items.forEach(item => {
        const name = (item.querySelector("#name")?.textContent || "").trim().toLowerCase()
        const value = (item.dataset.value || "").toLowerCase()
        if (name.startsWith(q) || value.startsWith(q)) {
          prefixMatches.push(item)
          item.classList.remove("hidden")
        } else if (name.includes(q) || value.includes(q)) {
          substringMatches.push(item)
          item.classList.remove("hidden")
        } else {
          item.classList.add("hidden")
        }
      })
      // przenosimy dopasowania na gore w kolejnosci: prefiksowe, potem substring
      const parent = items[0]?.parentNode
      if (parent) { prefixMatches.concat(substringMatches).forEach(item => parent.appendChild(item)) }
      return
    }

    clearTimeout(this.searchTimeout)
    const silent = event?.detail?.silent

    this.searchTimeout = setTimeout(() => {
      if (this.frame) {
        const fieldIds = Array.from(this.fieldHandler.fields).map(field => field.value).join(",")
        if (!silent) {
          this.showDropdown()
          this.showSpinner()
        }
        if (this.searchUrlValue.includes("?")) {
          this.frame.src = `${this.searchUrlValue}&q=${encodeURIComponent(this.inputTarget.value)}&frame=${this.frameIdValue}&ids=${fieldIds}`
        } else {
          this.frame.src = `${this.searchUrlValue}?q=${encodeURIComponent(this.inputTarget.value)}&frame=${this.frameIdValue}&ids=${fieldIds}`
        }
      } else {
        console.error(`Frame with ID ${this.frameIdValue} not found`)
      }
    }, 1000) // 1s
  }

  findElementByDataAttribute(container, attribute, value) {
    return container.querySelector(this.dataAttributeSelector(attribute, value))
  }

  // wszystkie elementy o danej wartości - ta sama pozycja może być w dropdownie kilka razy
  // (np. status należący do kilku lejków pokazuje się w sekcji każdego z nich)
  findElementsByDataAttribute(container, attribute, value) {
    return container.querySelectorAll(this.dataAttributeSelector(attribute, value))
  }

  dataAttributeSelector(attribute, value) {
    return `div[${attribute}="${CSS.escape(value)}"]`
  }

  toggleResult(event) {
    const value = this.getFieldValue(event)
    console.log("Toggling:", value)

    // single-select: klikniecie aktualnie wybranej opcji = re-submit z ta sama wartoscia.
    // Domyslny toggle-off (handleFieldChange usuwa + dodaje "") submitowal pusta wartosc
    // i czyscil pole. Zamiast tego ponownie wysylamy formularz z aktualnym value, co
    // pozwala np. ponownie zarchiwizowac email z zamkajacym statusem po reopen.
    // Czyszczenie wartosci w single-select robi sie przez opcje "Bez przypisania".
    if (!this.multiple && this.fieldHandler.hiddenFieldExists(this.fieldNameValue, value)) {
      event.intumReSubmit = true // sentinel — toggleBubble pominie usuniecie bombelka
      event.preventDefault()
      if (this.formValue) {
        const form = this.element.closest("form")
        if (form) {
          this.addOldValue()
          form.requestSubmit()
        }
      }
      return
    }

    this.select(value)
  }

  toggleSelection() {
    console.log("toggleSelection")
    this.fieldHandler.fields.forEach(field => this.toggleSearchFieldDetailsByValue(field.value))
  }

  toggleBubble(event) {
    if (event.intumReSubmit) return // single-select same-pick re-submit — nie ruszaj bombelka
    const id = this.getFieldValue(event)

    // dla single-select usuwamy stare bąbelki (różne od klikniętego id) - to oznacza zmianę wyboru.
    // bąbelek o tym samym id pomijamy - toggleResult (odpalany przed toggleBubble) już zatoggleował
    // jego stan w hidden fields i ikonach; my tylko usuniemy go z DOM jeśli ma być odznaczony.
    if (!this.multiple) {
      this.bubblesContainerTarget.querySelectorAll("div[data-bubble]").forEach(bubble => {
        if (bubble.dataset.bubble !== id) this.removeBubbleElement(bubble)
      })
    }

    const bubble = this.findElementByDataAttribute(this.bubblesContainerTarget, "data-bubble", id)
    console.log("toggling bubble:", bubble, id)

    if (bubble) this.removeBubble(event)
    else this.addBubble(event)
  }

  // usuwa bąbelek niezależnie od jego templatu (multi-mode ma button X, single-mode nie ma)
  // przechodzi przez select() — w single-select po zamianie wartosci toggleResult dodaje nowa wartosc
  // (fields=2, no-submit), potem usuniecie starego baka tutaj zbije fields do 1 i select() submituje formularz
  removeBubbleElement(bubble) {
    this.select(bubble.dataset.bubble)
    bubble.remove()
  }

  addBubble(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.value
    const name = event.currentTarget.querySelector("#name").querySelector("span").textContent
    const bubble = this.bubbleTemplateTarget.content.cloneNode(true)

    bubble.querySelector("#bubble").dataset.bubble = id
    bubble.querySelector("#name").textContent = name
    this.bubblesContainerTarget.prepend(bubble)
    const clearButton = this.bubblesContainerTarget.querySelector("#clearAllButton")
    if (clearButton) { clearButton.classList.remove("hidden") }
    this.clearInput()
  }

  removeBubble(event) {
    event.preventDefault()
    console.log("Removing bubble")
    // zarowno we frame istnieje div[data-value] jak i w bubble istnieje div[data-bubble], maja one korespondujace ze soba wartosci
    const value = this.getFieldValue(event)
    const field = this.findElementByDataAttribute(this.bubblesContainerTarget, "data-bubble", value)

    field.remove()
    // po usunięciu bąbelka focus na search input - user może od razu szukać/wpisywać
    this.inputTarget.focus()
  }

  // klik w wybrany single_value bubble: zamieniamy go na tekst w inpucie, zeby user
  // mogl edytowac/skasowac jak normalnie wpisany query. Po usunieciu bubble input
  // odsłania się (znika reguła group-has-[data-bubble]:hidden), dropdown otwiera,
  // tekst zaznaczony - user moze od razu pisac na nowo lub modyfikowac.
  editBubble(event) {
    event.preventDefault()
    event.stopPropagation()
    const bubble = event.currentTarget.closest("div[data-bubble]")
    if (!bubble) { return }

    const name = bubble.querySelector("#name").textContent.trim()

    // usuwa bąbelek + czyści hidden field (przez select() → handleFieldChange)
    this.removeBubbleElement(bubble)

    this.inputTarget.value = name
    this.showDropdown()
    this.inputTarget.focus()
    // kursor na koncu tekstu — user moze dopisywac/kasowac od tyłu, bez zaznaczenia
    this.inputTarget.setSelectionRange(name.length, name.length)
    // celowo bez search() — wyszukiwanie startuje dopiero gdy user faktycznie zmieni tekst
    // (przez data-action="input->intum-select#search" na inpucie)
  }

  select(value) {
    console.log("Selecting:", value)
    this.fieldHandler.handleFieldChange(value)
    if (this.hasComponentTarget) { this.componentTarget.dispatchEvent(new Event("change", { bubbles: true })) }
    // ukrywamy elementy z searcha tylko gdy frame jest juz załadowany
    if (this.frameLoaded) { this.toggleSearchFieldDetailsByValue(value) }

    // chcemy submitowac form tylko wtedy gdy jest to single select i jest tylko jeden element wyszukany
    // odpalamy toggleResult w _bubble.html.erb i zeby uniknac submitowania formularza, gdy sa jeszcze dwie wartosci w hidden fields
    if (this.formValue && this.fieldHandler.fields.length === 1) {
      const form = this.element.closest("form")
      if (form) {
        this.addOldValue() // opcjonalne dodanie pierwotnej wartości do formularza
        form.requestSubmit()
      }
    }
  }

  clearBubbles(_event) {
    const event1 = new Event("click")
    console.log("Removing all bubbles")
    this.bubblesContainerTarget.querySelectorAll("div[data-bubble]").forEach(bubble => {
      bubble.querySelector("button").dispatchEvent(event1)
    })

    this.bubblesContainerTarget.querySelector("#clearAllButton").classList.add("hidden")
  }

  // przełącza ikony na każdym wystąpieniu wartości w dropdownie, żeby duplikaty
  // (ten sam status w kilku lejkach) miały spójny stan zaznaczenia. Wartości może już
  // nie być na liście (np. po wyszukaniu innej frazy) - wtedy nie ma czego togglować.
  toggleSearchFieldDetailsByValue(value) {
    this.findElementsByDataAttribute(this.frame, "data-value", value).forEach(field => {
      this.toggleCrossIcon(field)
      this.toggleTickIcon(field)
    })
  }

  disconnect() {
    document.removeEventListener("click", this.hideDropdownHandler)
    if (this.repositionHandler) {
      window.removeEventListener("scroll", this.repositionHandler, true)
      window.removeEventListener("resize", this.repositionHandler)
    }
    if (this.visibilityObserver) { this.visibilityObserver.disconnect() }
    if (this.panelResizeObserver) { this.panelResizeObserver.disconnect() }
  }

  toggleTickIcon(target) { target.querySelector(".tick-icon").classList.toggle("hidden") }
  toggleCrossIcon(target) { target.querySelector(".cross-icon").classList.toggle("hidden") }
  hideSpinner() { this.element.querySelector("#spinner").classList.add("hidden") }
  showSpinner() { this.element.querySelector("#spinner").classList.remove("hidden") }
  showDropdown(event) {
    // stopPropagation potrzebne gdy showDropdown jest wywoływane z data-action na divku
    // (np. _single_value w trybie single-select). Bez tego document-level hideDropdown
    // sprawdza event.target - jeśli to nie INPUT/BUTTON, zamyka dropdown od razu.
    if (event) event.stopPropagation()
    if (this.open) { return }
    console.log("showDropdown", this.open)
    this.openDropdown()
  }

  toggleDropdown() {
    console.log("toggleDropdown", this.open)
    if (this.open) { this.closeDropdown() } else { this.openDropdown(); this.inputTarget.focus() }
  }

  // overlay tylko gdy włączony ORAZ przeglądarka wspiera Popover API - starsze (np. iOS < 17) nie mają
  // showPopover, więc lecimy zwykłym dropdownem (może być przycięty przez overflow, ale działa) zamiast crashować
  get useOverlay() {
    return this.overlayValue && typeof this.dropdownTarget.showPopover === "function"
  }

  // wspólne pokazanie listy: w trybie overlay przez natywny popover (top-layer), inaczej przez klasę hidden
  openDropdown() {
    if (this.useOverlay) {
      if (!this.dropdownTarget.matches(":popover-open")) { this.dropdownTarget.showPopover() }
      this.dropdownTarget.style.visibility = "visible"
      this.positionOverlay()
      this.repositionHandler = this.repositionHandler || this.positionOverlay.bind(this)
      window.addEventListener("scroll", this.repositionHandler, true)
      window.addEventListener("resize", this.repositionHandler)
      // widoczność listy sterowana widocznością triggera: gdy bąbelek zostanie przycięty przez
      // overflow boxa lub zjedzie poza viewport - lista znika (pozostaje otwarta, wraca gdy trigger znów widoczny)
      this.observeTriggerVisibility()
      this.observePanelResize()
    } else {
      this.dropdownTarget.classList.remove("hidden")
    }
    this.open = true
  }

  // wspólne ukrycie listy (odwrotność openDropdown)
  closeDropdown() {
    if (this.useOverlay) {
      if (this.repositionHandler) {
        window.removeEventListener("scroll", this.repositionHandler, true)
        window.removeEventListener("resize", this.repositionHandler)
      }
      if (this.visibilityObserver) { this.visibilityObserver.disconnect() }
      if (this.panelResizeObserver) { this.panelResizeObserver.disconnect() }
      this.lastPanelSize = null
      if (this.dropdownTarget.matches(":popover-open")) { this.dropdownTarget.hidePopover() }
    } else {
      this.dropdownTarget.classList.add("hidden")
    }
    this.open = false
  }

  // Lista pozycji dociaga sie turbo framem PO otwarciu (przy otwarciu panel ma tylko input i spinner),
  // a przy wpisywaniu w wyszukiwarke jeszcze sie filtruje - czyli panel zmienia szerokosc po tym, jak
  // policzylismy pozycje. Bez tego observera left zostaje wyliczone dla waskiego panelu, panel rosnie
  // w prawo od ustalonego left i przestaje stykac sie prawa krawedzia z bąbelkiem; poprawialo sie
  // dopiero przy scrollu (bo dopiero on wolal positionOverlay ponownie).
  observePanelResize() {
    if (typeof ResizeObserver !== "function") { return } // starsze przeglądarki - zostaje pozycja z otwarcia
    if (this.panelResizeObserver) { this.panelResizeObserver.disconnect() }

    this.panelResizeObserver = new ResizeObserver((entries) => {
      if (!this.open) { return }
      const { width, height } = entries[0].contentRect
      // przeliczamy tylko przy FAKTYCZNEJ zmianie rozmiaru - positionOverlay rusza stylami panelu,
      // wiec bezwarunkowe wolanie groziloby petla observer -> styl -> observer
      if (this.lastPanelSize && this.lastPanelSize.width === width && this.lastPanelSize.height === height) { return }
      this.lastPanelSize = { width, height }
      this.positionOverlay()
    })
    this.panelResizeObserver.observe(this.dropdownTarget)
  }

  // IntersectionObserver liczy widoczność triggera z uwzględnieniem przycięcia przez pośrednie
  // kontenery z overflow (box zadań) - gdy trigger zniknie, chowamy listę; gdy wraca - pokazujemy i repozycjonujemy
  observeTriggerVisibility() {
    if (this.visibilityObserver) { this.visibilityObserver.disconnect() } // symetrycznie do observePanelResize

    this.visibilityObserver = new IntersectionObserver((entries) => {
      const visible = entries[0].isIntersecting
      this.dropdownTarget.style.visibility = visible ? "visible" : "hidden"
      if (visible) { this.positionOverlay() }
    }, { threshold: 0 })
    this.visibilityObserver.observe(this.element)
  }

  // pozycjonuje popover (fixed) względem triggera; kierunek z position (left/force_left = od lewej krawędzi,
  // inaczej od prawej - tak jak left-0/right-0 w trybie non-overlay), flip w górę gdy nie mieści się na dole
  positionOverlay() {
    const trigger = this.element.getBoundingClientRect()
    const panel = this.dropdownTarget
    panel.style.margin = "0"
    panel.style.inset = "auto"
    panel.style.position = "fixed"
    const rect = panel.getBoundingClientRect()
    const alignLeft = ["left", "force_left"].includes(this.positionValue)
    let left = alignLeft ? trigger.left : trigger.right - rect.width
    let top = trigger.bottom + 4
    if (top + rect.height > window.innerHeight - 8) { top = trigger.top - rect.height - 4 }
    if (left < 8) { left = 8 }
    if (left + rect.width > window.innerWidth - 8) { left = window.innerWidth - rect.width - 8 }
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  clearInput() { this.inputTarget.value = "" }
  hideDropdown(event) {
    if (!this.open) { return }

    // sprawdzamy czy event.target jest inputem lub buttonem, ale zahaczamy tez o elementy w srodku buttona, np divy
    const isInputOrButton = (element, depth = 5) => {
      if (!element || depth === 0) return false
      if (element.tagName === "INPUT" || element.tagName === "BUTTON") return true
      return isInputOrButton(element.parentElement, depth - 1)
    }

    if (!this.element.contains(event.target) || !isInputOrButton(event.target)) {
      console.log("hiding dropdown", this.open)
      this.closeDropdown()
    }
  }

  getFieldValue(event) {
    const closestDiv = event.currentTarget.closest("div[data-value]") || event.currentTarget.closest("div[data-bubble]")
    return closestDiv.dataset.value || closestDiv.dataset.bubble
  }

  // wsparcie wysyłania pierwotnej wartości pola / pól dla single_select, bulk_action
  addOldValue() {
    if (this.oldValue["send"] === false) { return }

    if (this.nameValue === "single_select") {
      this.fieldHandler.addHiddenField(`old_${this.fieldNameValue}`, this.oldValue["value"])
    }

    if (this.nameValue === "bulk_action") {
      const fieldName = this.fieldNameValue.endsWith("[]") ? this.fieldNameValue.slice(0, -2) : this.fieldNameValue

      // dla poprawnego działania bulk action z opcją old, pozyskujemy z widoku dokumenty stare wartości
      document.querySelectorAll(`div#bulk_action_old[data-name-id="${this.oldValue["name_id"]}"]`).forEach(el => {
        const id = el.dataset.id
        const oldValue = el.dataset.value
        this.fieldHandler.addHiddenField(`old_${fieldName}[${id}]`, oldValue)
      })
    }
  }
}
